import styled, { css } from 'styled-components';
import { Icon } from 'src/components';
import { SessionColor } from 'src/apps/LearningSession/sessionConfig';

export const Header = styled.header<{ $color: SessionColor }>(({ theme, $color }) => {
  const { co } = theme;
  const bgColor =
    $color === 'green' ? co.success80 : $color === 'red' ? co.error60 : co.information80;
  const secondaryIconFill =
    $color === 'green' ? co.success40 : $color === 'red' ? co.error20 : co.information50;
  return css`
    background: ${bgColor};
    display: flex;
    padding: 0 ${theme.sz.s4};
    width: 100%;
    z-index: ${theme.zi.header};
    min-height: ${theme.sz.s12};

    & * {
      fill: ${secondaryIconFill};
    }

    @media (min-width: 30rem) {
      padding: ${theme.sz.s4};
    }
  `;
});

export const HeaderContents = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: ${({ theme }) => theme.sz.xxl};
  width: 100%;
`;

export const Title = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral20};
    flex-shrink: 2;
    font-family: ${theme.DEPRECATED_ty.boing};
    font-weight: ${theme.DEPRECATED_ty.bold};
    font-size: ${theme.DEPRECATED_ty.large};
    margin-right: ${theme.sz.s4};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    /* Required for ellipsis */
    & > button {
      width: 100%;
    }

    @media (min-width: 30rem) {
      margin: auto auto auto ${theme.sz.s2};
    }

    @media (min-width: 40rem) {
      font-size: ${theme.DEPRECATED_ty.larger};
    }
  `,
);

export const IconReleaseTagWrapper = styled.div`
  display: none;
  position: relative;

  @media (min-width: 30rem) {
    display: inline-block;
  }
`;

export const SessionIcon = styled(Icon)(
  ({ theme }) => css`
    &,
    svg {
      height: ${theme.sz.s10};
      width: ${theme.sz.s10};
    }
    & * {
      fill: #ffffff;
    }
  `,
);

export const IconButton = styled.button`
  background: none;
  border: none;
  display: inline-block;
  margin-right: ${({ theme }) => theme.sz.s3};
`;

export const CustomiseFeaturesWrapper = styled.div<{ $coachmarkShown: boolean }>(
  ({ theme, $coachmarkShown }) => css`
    position: relative;
    z-index: ${$coachmarkShown ? theme.zi.modal + 1 : 'auto'};
  `,
);

export const CustomiseFeaturesButton = styled(IconButton)<{
  $showLabel: boolean;
  $coachmarkShown: boolean;
}>(
  ({ theme, $showLabel, $coachmarkShown }) => css`
    align-items: center;
    color: ${$coachmarkShown ? theme.co.secondary20 : theme.co.secondary40};
    cursor: pointer;
    gap: ${theme.sz.s2};
    position: relative;
    transition:
      background 240ms ease-out,
      border-color 240ms ease-out,
      box-shadow 240ms ease-out,
      color 240ms ease-out;
    z-index: ${$coachmarkShown ? theme.zi.modal + 2 : 'auto'};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    ${
      $showLabel &&
      css`
        background: ${$coachmarkShown ? theme.co.primary50 : 'transparent'};
        border: ${theme.bo.mediumWidth} solid
          ${$coachmarkShown ? theme.co.secondary20 : theme.co.secondary40};
        border-radius: ${theme.bo.round};
        display: inline-flex;
        min-height: ${theme.sz.s10};
        padding: ${theme.sz.s1} ${theme.sz.s3};

        & *,
        & svg {
          fill: ${$coachmarkShown ? theme.co.secondary20 : theme.co.secondary40};
          transition: fill 240ms ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          & *,
          & svg {
            transition: none;
          }
        }

        &:focus-visible {
          outline: ${theme.bo.thickWidth} solid ${theme.co.secondary40};
          outline-offset: ${theme.bo.thinWidth};
        }

        ${
          $coachmarkShown &&
          css`
            box-shadow: 0 0 0 ${theme.bo.thickWidth} ${theme.co.neutral99};
          `
        }
      `
    }
  `,
);

export const CustomiseFeaturesLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-weight: ${theme.DEPRECATED_ty.bold};
    white-space: nowrap;
  `,
);

export const KeyboardIconButton = styled(IconButton)`
  display: none;

  @media (min-width: 45rem) {
    display: inline-block;
  }
`;

export const KeyboardShortcutIcon = styled(Icon)<{ color: SessionColor }>(
  ({ theme }) => css`
    cursor: pointer;
    font-size: ${theme.sz.s8};
  `,
);

export const XIcon = styled(Icon)<{ color: SessionColor }>(
  ({ theme }) => css`
    flex-shrink: 1;
    font-size: ${theme.sz.s8};
  `,
);

export const TransparentButton = styled.button.attrs({
  type: 'button',
})`
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  text-overflow: inherit;
  white-space: inherit;
  overflow: inherit;
`;

export const HeaderIconsWrapper = styled.div`
  display: flex;
  align-items: center;
`;
